import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Permission {
  canViewEmployees: boolean;
  canCreateEmployees: boolean;
  canEditEmployees: boolean;
  canDeleteEmployees: boolean;
  canViewDepartments: boolean;
  canEditDepartments: boolean;
  canViewDevices: boolean;
  canEditDevices: boolean;
  canSyncDevices: boolean;
  canViewAttendance: boolean;
  canViewReports: boolean;
  isAdmin: boolean;
}

export type UserRole = 'admin' | 'manager' | 'hr' | 'department_manager' | 'user';

interface User {
  id: number;
  name: string;
  email: string;
  role: UserRole;
}

interface PermissionContextType {
  user: User | null;
  permissions: Permission;
  isLoading: boolean;
  hasPermission: (permission: keyof Permission) => boolean;
  setUser: (user: User | null) => void;
}

const defaultPermissions: Permission = {
  canViewEmployees: false,
  canCreateEmployees: false,
  canEditEmployees: false,
  canDeleteEmployees: false,
  canViewDepartments: false,
  canEditDepartments: false,
  canViewDevices: false,
  canEditDevices: false,
  canSyncDevices: false,
  canViewAttendance: false,
  canViewReports: false,
  isAdmin: false,
};

const rolePermissions: Record<UserRole, Permission> = {
  admin: {
    canViewEmployees: true,
    canCreateEmployees: true,
    canEditEmployees: true,
    canDeleteEmployees: true,
    canViewDepartments: true,
    canEditDepartments: true,
    canViewDevices: true,
    canEditDevices: true,
    canSyncDevices: true,
    canViewAttendance: true,
    canViewReports: true,
    isAdmin: true,
  },
  manager: {
    canViewEmployees: true,
    canCreateEmployees: true,
    canEditEmployees: true,
    canDeleteEmployees: false,
    canViewDepartments: true,
    canEditDepartments: false,
    canViewDevices: true,
    canEditDevices: false,
    canSyncDevices: true,
    canViewAttendance: true,
    canViewReports: true,
    isAdmin: false,
  },
  hr: {
    canViewEmployees: true,
    canCreateEmployees: true,
    canEditEmployees: true,
    canDeleteEmployees: false,
    canViewDepartments: true,
    canEditDepartments: false,
    canViewDevices: false,
    canEditDevices: false,
    canSyncDevices: false,
    canViewAttendance: true,
    canViewReports: true,
    isAdmin: false,
  },
  department_manager: {
    canViewEmployees: false,
    canCreateEmployees: false,
    canEditEmployees: false,
    canDeleteEmployees: false,
    canViewDepartments: false,
    canEditDepartments: false,
    canViewDevices: false,
    canEditDevices: false,
    canSyncDevices: false,
    canViewAttendance: true,
    canViewReports: true,
    isAdmin: false,
  },
  user: {
    canViewEmployees: true,
    canCreateEmployees: false,
    canEditEmployees: false,
    canDeleteEmployees: false,
    canViewDepartments: true,
    canEditDepartments: false,
    canViewDevices: false,
    canEditDevices: false,
    canSyncDevices: false,
    canViewAttendance: false,
    canViewReports: false,
    isAdmin: false,
  },
};

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission>(defaultPermissions);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulating user fetch - in real app, this would come from auth
    // For now, default to admin for development
    const storedRole = localStorage.getItem('userRole') as UserRole || 'admin';
    const mockUser: User = {
      id: 1,
      name: 'Admin User',
      email: 'admin@example.com',
      role: storedRole,
    };
    setUser(mockUser);
    setPermissions(rolePermissions[storedRole]);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      setPermissions(rolePermissions[user.role]);
      localStorage.setItem('userRole', user.role);
    }
  }, [user]);

  const hasPermission = (permission: keyof Permission): boolean => {
    return permissions[permission] === true;
  };

  return (
    <PermissionContext.Provider value={{ user, permissions, isLoading, hasPermission, setUser }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
